/**
 * Prayer times, the adhan overlay state machine, qibla and Ramadan.
 *
 * Implements docs/02 §4 (time and prayer) and §5 (qibla), plus the Ramadan
 * rules from docs/01 §8.
 *
 * ── Three rules this module never breaks ──────────────────────────────────
 *
 *  1. **Nothing here reads the ambient clock.** Every entry point takes an
 *     explicit `now: number` (corrected UTC epoch ms) or a `TrustedClock`.
 *     `adhan`'s own `PrayerTimes.currentPrayer()` / `.nextPrayer()` default
 *     their date argument to `new Date()`; the wrappers below always pass an
 *     explicit instant, and `prayer.test.ts` proves it by installing a Date
 *     that throws on any zero-argument construction.
 *
 *  2. **Nothing here reads the platform timezone.** `adhan`'s `PrayerTimes`
 *     reads only `getFullYear()` / `getMonth()` / `getDate()` off the Date it
 *     is handed (verified against the adhan 4.4.6 source), so the anchor Date
 *     is built at *local noon* — the one construction whose calendar
 *     components are the requested ones in every host timezone, with 12 hours
 *     of slack on either side for any DST shift. Everything returned is a UTC
 *     epoch instant. All Cairo formatting goes through `time.ts`, which
 *     computes Egypt's DST rule itself rather than trusting the device tzdata.
 *
 *  3. **Everything is a pure function of its arguments.** No caching, no
 *     module-level mutable state, no mutation of inputs. Two offline devices
 *     handed the same settings and the same instant compute byte-identical
 *     results with no coordination.
 *
 * ── Two day reckonings live in this file, deliberately ────────────────────
 *
 * `DayKey` is just 'YYYY-MM-DD'; which reckoning a given key is in depends on
 * the function, and every signature says which:
 *
 *  - **Calendar date** — `prayerTimesFor`, `prayerTimeline`, `iftarAt`,
 *    `suhoorEndsAt`, `imsakAt`. Prayer times are astronomical: the ordering
 *    fajr < sunrise < dhuhr < asr < maghrib < isha only holds within a
 *    Gregorian calendar date, and a fast runs [fajr(D), maghrib(D)) on one
 *    calendar date. Keying these by the office's 05:00-rollover day would
 *    split a single fast across two keys.
 *
 *  - **Office rollover day** (docs/01, the cross-cutting rule) —
 *    `prayersInOfficeDay`, `isRamadanAt`, `ramadanState`. These answer
 *    "what does the board show today", and today is 05:00 → 05:00.
 *
 * The two genuinely differ here. Cairo fajr is around 04:07 local at
 * midsummer and around 04:47 during Ramadan 2026 — both *before* the 05:00
 * rollover — so fajr on calendar date D belongs to office day D-1 for much of
 * the year. `prayersInOfficeDay` is the function that gets that right.
 */

import { CalculationMethod, Coordinates, Madhab, PrayerTimes as AdhanPrayerTimes } from 'adhan'

import type { Deg } from './units.js'
import { normDeg, degToRad, radToDeg, deg, rad } from './units.js'
import type { DayKey, TrustedClock } from './time.js'
import {
  DEFAULT_ROLLOVER_HOUR,
  addDays,
  daysBetween,
  formatCairoDate,
  formatCairoTime,
  rolloverDayBounds,
  rolloverDayOf,
} from './time.js'
import type { RamadanPeriod, Settings } from './entities/misc.js'

// ─────────────────────────────────────────────────────────────────────────────
// 1. Prayers and settings
// ─────────────────────────────────────────────────────────────────────────────

export type PrayerName = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha'

/**
 * The six markers that order a day, chronologically. Sunrise is in the list
 * because it bounds the fajr window, not because it is a prayer.
 */
export const PRAYER_ORDER = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'] as const

/**
 * The five prayers that actually call an adhan. Sunrise does not, so it must
 * never raise the overlay.
 */
export const ADHAN_PRAYERS = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const

/** Position of a prayer in the daily order. Used only as a sort tie-break. */
const PRAYER_INDEX: Readonly<Record<PrayerName, number>> = Object.freeze({
  fajr: 0,
  sunrise: 1,
  dhuhr: 2,
  asr: 3,
  maghrib: 4,
  isha: 5,
})

/** Keys of `adhan`'s CalculationMethod table. Asserted against it in tests. */
export type CalculationMethodKey =
  | 'MuslimWorldLeague'
  | 'Egyptian'
  | 'Karachi'
  | 'UmmAlQura'
  | 'Dubai'
  | 'MoonsightingCommittee'
  | 'NorthAmerica'
  | 'Kuwait'
  | 'Qatar'
  | 'Singapore'
  | 'Tehran'
  | 'Turkey'
  | 'Other'

export const CALCULATION_METHODS = [
  'MuslimWorldLeague',
  'Egyptian',
  'Karachi',
  'UmmAlQura',
  'Dubai',
  'MoonsightingCommittee',
  'NorthAmerica',
  'Kuwait',
  'Qatar',
  'Singapore',
  'Tehran',
  'Turkey',
  'Other',
] as const satisfies readonly CalculationMethodKey[]

export type MadhabKey = 'shafi' | 'hanafi'

/**
 * Everything `prayerTimesFor` needs. Deliberately a plain value type: it is
 * serialisable, comparable, and carries no clock.
 *
 * `Settings` (entities/misc.ts) carries latitude, longitude and the rollover
 * hour but not the calculation method or the madhab, so `prayerSettingsFrom`
 * fills those in with the office's confirmed choice (Egyptian / Shafi).
 */
export interface PrayerSettings {
  latitude: number
  longitude: number
  method: CalculationMethodKey
  madhab: MadhabKey
  /** Per-prayer nudge in whole minutes. Always present; zeros by default. */
  adjustments: Readonly<Record<PrayerName, number>>
  /** Office day boundary, 0-23. Only the office-day helpers consult it. */
  rolloverHour: number
}

/** The office, New Cairo (docs/02 §5). */
export const OFFICE_COORDINATES = Object.freeze({
  latitude: 30.031244,
  longitude: 31.549313,
})

export const NO_ADJUSTMENTS: Readonly<Record<PrayerName, number>> = Object.freeze({
  fajr: 0,
  sunrise: 0,
  dhuhr: 0,
  asr: 0,
  maghrib: 0,
  isha: 0,
})

/**
 * Egyptian General Authority of Survey (19.5° fajr / 17.5° isha, +1 min
 * dhuhr) with the standard, non-Hanafi Asr — docs/02 §4.
 */
export const DEFAULT_PRAYER_SETTINGS: PrayerSettings = Object.freeze({
  latitude: OFFICE_COORDINATES.latitude,
  longitude: OFFICE_COORDINATES.longitude,
  method: 'Egyptian',
  madhab: Madhab.Shafi,
  adjustments: NO_ADJUSTMENTS,
  rolloverHour: DEFAULT_ROLLOVER_HOUR,
})

/** Lift the persisted `Settings` record into prayer-calculation settings. */
export function prayerSettingsFrom(
  settings: Pick<Settings, 'latitude' | 'longitude' | 'dayRolloverHour'>,
): PrayerSettings {
  return {
    latitude: settings.latitude,
    longitude: settings.longitude,
    method: DEFAULT_PRAYER_SETTINGS.method,
    madhab: DEFAULT_PRAYER_SETTINGS.madhab,
    adjustments: DEFAULT_PRAYER_SETTINGS.adjustments,
    rolloverHour: settings.dayRolloverHour,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Day keys
// ─────────────────────────────────────────────────────────────────────────────

const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/

interface Ymd {
  y: number
  m: number
  d: number
}

/**
 * Parse and validate a 'YYYY-MM-DD' key. Rejects impossible dates.
 *
 * The round-trip also rejects years 0000-0099, and deliberately so: both
 * `Date.UTC` and the `new Date(y, m, d, …)` constructor silently remap a
 * two-digit year to 19xx, so '0050-06-21' would otherwise compute 1950's
 * prayer times and label them 0050. A throw beats a plausible wrong instant.
 */
function parseDayKey(day: DayKey): Ymd {
  if (!DAY_KEY_RE.test(day)) throw new RangeError(`not a DayKey: ${JSON.stringify(day)}`)
  const y = Number(day.slice(0, 4))
  const m = Number(day.slice(5, 7))
  const d = Number(day.slice(8, 10))
  // Round-trip through UTC to reject 2026-02-30 and friends.
  const probe = new Date(Date.UTC(y, m - 1, d))
  if (
    probe.getUTCFullYear() !== y ||
    probe.getUTCMonth() + 1 !== m ||
    probe.getUTCDate() !== d
  ) {
    throw new RangeError(`not a real calendar date: ${day}`)
  }
  return { y, m, d }
}

/** The Cairo *calendar* date an instant falls on. Not the office day. */
export function calendarDayOf(now: number): DayKey {
  requireInstant(now, 'calendarDayOf')
  return formatCairoDate(now) as DayKey
}

/** The office *rollover* day an instant falls on (05:00 boundary). */
export function officeDayOf(now: number, settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS): DayKey {
  requireInstant(now, 'officeDayOf')
  return rolloverDayOf(now, settings.rolloverHour)
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Prayer times
// ─────────────────────────────────────────────────────────────────────────────

/** The six instants of one Gregorian calendar date, as UTC epoch ms. */
export interface DayPrayerTimes {
  /** The Gregorian calendar date these instants belong to. */
  day: DayKey
  fajr: number
  sunrise: number
  dhuhr: number
  asr: number
  maghrib: number
  isha: number
}

/** One prayer instant, tagged with the calendar date whose table produced it. */
export interface PrayerAt {
  prayer: PrayerName
  /** UTC epoch ms. */
  at: number
  /** Calendar date of the table this came from. */
  day: DayKey
}

function calculationParameters(settings: PrayerSettings) {
  // CalculationMethod factories return a fresh object every call, so mutating
  // it here mutates nothing the caller owns.
  const params = CalculationMethod[settings.method]()
  params.madhab = settings.madhab
  const a = settings.adjustments
  params.adjustments = {
    fajr: a.fajr,
    sunrise: a.sunrise,
    dhuhr: a.dhuhr,
    asr: a.asr,
    maghrib: a.maghrib,
    isha: a.isha,
  }
  return params
}

/**
 * The six prayer instants of a Gregorian calendar date, as UTC epoch ms.
 *
 * `day` is a CALENDAR date, not the office rollover day — see the module
 * header. Computed in UTC and never formatted with the platform timezone.
 *
 * Throws if the location and date produce no sunrise or sunset (polar day or
 * night). Silently returning NaN would propagate an unnoticed wrong instant
 * into the overlay, which is worse.
 *
 * Throws, for the same reason, on coordinates that are not a location. This is
 * not defensive decoration: the settings row arrives over sync from another
 * device and nothing revalidates it on the way in, and `null` coordinates do
 * NOT produce NaN — they coerce to 0 inside the trigonometry and return a
 * complete, plausible, finite prayer table for a point in the Gulf of Guinea.
 * The adhan would then fire on the wall, on time, for the wrong hemisphere,
 * with nothing on screen suggesting anything was wrong.
 */
export function prayerTimesFor(
  day: DayKey,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
): DayPrayerTimes {
  requireLocation(settings)
  const { y, m, d } = parseDayKey(day)
  // Local NOON. adhan reads only the calendar components off this Date, and
  // noon is the one hour that survives every host timezone and every DST jump
  // with the requested date intact.
  const anchor = new Date(y, m - 1, d, 12, 0, 0, 0)
  const t = new AdhanPrayerTimes(
    new Coordinates(settings.latitude, settings.longitude),
    anchor,
    calculationParameters(settings),
  )
  const out: DayPrayerTimes = {
    day,
    fajr: t.fajr.getTime(),
    sunrise: t.sunrise.getTime(),
    dhuhr: t.dhuhr.getTime(),
    asr: t.asr.getTime(),
    maghrib: t.maghrib.getTime(),
    isha: t.isha.getTime(),
  }
  for (const name of PRAYER_ORDER) {
    if (!Number.isFinite(out[name])) {
      throw new RangeError(
        `${name} is undefined at ${settings.latitude},${settings.longitude} on ${day} (polar day or night)`,
      )
    }
  }
  return out
}

/**
 * The prayer instants of one calendar date as a chronologically sorted list.
 * Sorting is explicit rather than assumed: a per-prayer adjustment large
 * enough to reorder the day must not silently corrupt the timeline.
 */
export function prayerTimeline(
  day: DayKey,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  names: readonly PrayerName[] = PRAYER_ORDER,
): PrayerAt[] {
  const times = prayerTimesFor(day, settings)
  return names
    .map((prayer): PrayerAt => ({ prayer, at: times[prayer], day }))
    .sort((x, z) => x.at - z.at || PRAYER_INDEX[x.prayer] - PRAYER_INDEX[z.prayer])
}

/** 'HH:MM' Cairo wall-clock strings, via time.ts's own DST rule. */
export function formatPrayerTimes(times: DayPrayerTimes): Record<PrayerName, string> {
  return {
    fajr: formatCairoTime(times.fajr),
    sunrise: formatCairoTime(times.sunrise),
    dhuhr: formatCairoTime(times.dhuhr),
    asr: formatCairoTime(times.asr),
    maghrib: formatCairoTime(times.maghrib),
    isha: formatCairoTime(times.isha),
  }
}

/** How far either side of `now` the scanners will look before giving up. */
const MAX_SCAN_DAYS = 7

const MS_PER_DAY = 86_400_000

/**
 * How many whole days an instant can be dragged out of its own calendar date
 * by `settings.adjustments`, plus one.
 *
 * The scanners walk calendar days and pick the extremum, and this is the width
 * of the window they must look at. With the office's zero adjustments it is 1,
 * which is exactly the "isha(D) < fajr(D+1), fajr(D) after 00:00" reality. A
 * large enough adjustment breaks that ordering ACROSS days — `prayerTimeline`
 * re-sorts within a day, but a first-hit day-by-day scan would then return an
 * instant that is not the true minimum. Widening the window here is what makes
 * `nextPrayer`/`currentPrayer` agree with an exhaustive scan for any
 * adjustment, not just small ones.
 */
function scanSlackDays(settings: PrayerSettings): number {
  let maxAbsMinutes = 0
  for (const name of PRAYER_ORDER) {
    const a = settings.adjustments[name]
    if (Number.isFinite(a)) maxAbsMinutes = Math.max(maxAbsMinutes, Math.abs(a))
  }
  return 1 + Math.ceil((maxAbsMinutes * 60_000) / MS_PER_DAY)
}

/**
 * The next prayer strictly after `now`.
 *
 * Rolls past isha into the NEXT calendar day's fajr, which is the case
 * `adhan`'s own `nextPrayer()` answers with 'none'. Never returns 'none'.
 *
 * `now` is a corrected UTC epoch instant and is always supplied: this wrapper
 * exists precisely so the library's `date = new Date()` default is never used.
 */
export function nextPrayer(
  now: number,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  names: readonly PrayerName[] = PRAYER_ORDER,
): PrayerAt {
  requireInstant(now, 'nextPrayer')
  const slack = scanSlackDays(settings)
  const d0 = calendarDayOf(now)
  let best: PrayerAt | null = null
  let hitK: number | null = null
  // Days are visited in increasing order, so on a tie the earlier day and the
  // earlier position in PRAYER_ORDER win — the same answer a global sort gives.
  for (let k = -slack; k <= MAX_SCAN_DAYS; k++) {
    for (const e of prayerTimeline(addDays(d0, k), settings, names)) {
      if (e.at <= now) continue
      if (best === null || e.at < best.at) best = e
    }
    if (best !== null) {
      if (hitK === null) hitK = k
      else if (k - hitK >= slack) break
    }
  }
  if (best === null) throw new RangeError(`no prayer within ${MAX_SCAN_DAYS} days after ${now}`)
  return best
}

/**
 * The most recent prayer at or before `now`.
 *
 * Unlike `adhan`'s `currentPrayer()` this never returns 'none': before fajr
 * the answer is yesterday's isha, which is the window the user is actually in.
 */
export function currentPrayer(
  now: number,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  names: readonly PrayerName[] = PRAYER_ORDER,
): PrayerAt {
  requireInstant(now, 'currentPrayer')
  const slack = scanSlackDays(settings)
  const d0 = calendarDayOf(now)
  let best: PrayerAt | null = null
  let hitK: number | null = null
  // Days are visited in decreasing order, so on a tie the later day wins; the
  // extra same-day clause makes the later position in PRAYER_ORDER win too.
  for (let k = slack; k >= -MAX_SCAN_DAYS; k--) {
    for (const e of prayerTimeline(addDays(d0, k), settings, names)) {
      if (e.at > now) continue
      if (
        best === null ||
        e.at > best.at ||
        (e.at === best.at && e.day === best.day && PRAYER_INDEX[e.prayer] > PRAYER_INDEX[best.prayer])
      ) {
        best = e
      }
    }
    if (best !== null) {
      if (hitK === null) hitK = k
      else if (hitK - k >= slack) break
    }
  }
  if (best === null) throw new RangeError(`no prayer within ${MAX_SCAN_DAYS} days before ${now}`)
  return best
}

/** Milliseconds until the next prayer. Always > 0. */
export function msUntilNextPrayer(
  now: number,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  names: readonly PrayerName[] = PRAYER_ORDER,
): number {
  return nextPrayer(now, settings, names).at - now
}

/**
 * Every prayer instant falling inside one OFFICE day — 05:00 to 05:00, on the
 * half-open interval `[start, end)`.
 *
 * This is not the same set as `prayerTimeline` of the same key. During
 * Ramadan 2026 and all summer, Cairo fajr is before 05:00, so office day D
 * contains fajr of calendar day D+1 and not fajr of calendar day D. The DST
 * transition days are 23 and 25 hours long and this handles them, because
 * `rolloverDayBounds` does.
 */
export function prayersInOfficeDay(
  day: DayKey,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  names: readonly PrayerName[] = PRAYER_ORDER,
): PrayerAt[] {
  const { startUtc, endUtc } = rolloverDayBounds(day, settings.rolloverHour)
  const out: PrayerAt[] = []
  // An office day spans at most 25 hours, so with no adjustments calendar days
  // D-1..D+1 cover it. `scanSlackDays` widens that when an adjustment can drag
  // an instant further out of its own calendar date; without it an office day
  // could silently LOSE a prayer, breaking the partition.
  const slack = scanSlackDays(settings)
  for (let k = -slack; k <= slack; k++) {
    for (const e of prayerTimeline(addDays(day, k), settings, names)) {
      if (e.at >= startUtc && e.at < endUtc) out.push(e)
    }
  }
  return out.sort((x, z) => x.at - z.at || PRAYER_INDEX[x.prayer] - PRAYER_INDEX[z.prayer])
}

/** Convenience for callers holding a clock rather than a bare instant. */
export function nextPrayerOnClock(
  clock: TrustedClock,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  names: readonly PrayerName[] = PRAYER_ORDER,
): PrayerAt {
  return nextPrayer(clock.now(), settings, names)
}

/** Convenience for callers holding a clock rather than a bare instant. */
export function currentPrayerOnClock(
  clock: TrustedClock,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  names: readonly PrayerName[] = PRAYER_ORDER,
): PrayerAt {
  return currentPrayer(clock.now(), settings, names)
}

function requireInstant(now: number, who: string): void {
  if (!Number.isFinite(now)) {
    throw new RangeError(`${who}: now must be a finite UTC epoch ms, got ${String(now)}`)
  }
}

/** A latitude and longitude that name a real point on the planet. */
function requireLocation(settings: Pick<PrayerSettings, 'latitude' | 'longitude'>): void {
  const { latitude, longitude } = settings
  if (!Number.isFinite(latitude) || Math.abs(latitude) > 90) {
    throw new RangeError(`latitude must be a number in [-90, 90], got ${String(latitude)}`)
  }
  if (!Number.isFinite(longitude)) {
    throw new RangeError(`longitude must be a finite number, got ${String(longitude)}`)
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. The adhan overlay state machine
// ─────────────────────────────────────────────────────────────────────────────

/**
 * docs/01 (user decisions), restated so the rules are visible at the code:
 *
 *  - The overlay appears AT prayer time and any tap dismisses it.
 *  - A dismissal STICKS FOR THAT PRAYER WINDOW. It must not come back thirty
 *    seconds later. It comes back at the NEXT prayer.
 *  - The pre-adhan countdown is a separate thing and is never dismissed.
 *  - No auto status changes. No Jummah special case. Both explicitly ruled out.
 */
export interface AdhanConfig {
  /** Which prayers raise the overlay. Sunrise never does. */
  prayers: readonly PrayerName[]
  /**
   * How long an untouched overlay stays up before it retires itself.
   * `Number.POSITIVE_INFINITY` means "stay until dismissed or the next prayer".
   */
  visibleForMs: number
  /** Lead time for the pre-adhan countdown. Never dismissible. */
  countdownLeadMs: number
}

export const DEFAULT_ADHAN_CONFIG: AdhanConfig = Object.freeze({
  prayers: ADHAN_PRAYERS,
  visibleForMs: 5 * 60_000,
  // docs/01 §9: the prayer widget spikes to 900 within 10 minutes of a prayer.
  countdownLeadMs: 10 * 60_000,
})

/**
 * Stable identity of one prayer window.
 *
 * The exact UTC instant is the key, not the day plus the name: it is derived
 * identically on every device from the same settings, it survives a device
 * being offline across a rollover, and it makes "the same prayer tomorrow" a
 * different window with no extra bookkeeping.
 */
export function adhanWindowKey(prayer: PrayerName, at: number): string {
  return `${prayer}@${at}`
}

/** One tap. Append-only; replaying it is a no-op. */
export interface AdhanDismissal {
  prayer: PrayerName
  /** The prayer's exact instant — the window identity. */
  prayerAt: number
  /** When the tap happened. Recorded for the log; the reducer ignores it. */
  at: number
}

export interface AdhanOverlayState {
  /** Render the overlay? */
  visible: boolean
  /**
   * The window `now` sits in, whether or not the overlay is showing. Null only
   * before the first prayer the schedule knows about.
   */
  prayer: PrayerName | null
  /** When the window opened — the prayer instant itself. */
  since: number | null
  /** Identity of that window. Null exactly when `prayer` is null. */
  windowKey: string | null
  /** This window was dismissed by a tap. */
  dismissed: boolean
  /** This window aged out on its own rather than being dismissed. */
  expired: boolean
}

const NO_OVERLAY: AdhanOverlayState = Object.freeze({
  visible: false,
  prayer: null,
  since: null,
  windowKey: null,
  dismissed: false,
  expired: false,
})

/**
 * Where the overlay's prayer instants come from: either the settings to derive
 * them from (the tablet's normal path — it rolls across days and DST by
 * itself), or an already-computed list (a precomputed offline table, or a
 * test).
 */
export type AdhanSchedule = PrayerSettings | readonly PrayerAt[]

function isPrayerSettings(s: AdhanSchedule): s is PrayerSettings {
  return !Array.isArray(s)
}

/**
 * A misconfigured overlay is worse than a loud one: a negative `visibleForMs`
 * silently means "the adhan overlay never appears", which is the single thing
 * on this dashboard nobody would notice was broken until they missed a prayer.
 * Reject it instead. Zero is legal (and inert) — negative and NaN are not.
 */
function requireConfig(config: AdhanConfig): void {
  if (Number.isNaN(config.visibleForMs) || config.visibleForMs < 0) {
    throw new RangeError(`adhan visibleForMs must be >= 0, got ${String(config.visibleForMs)}`)
  }
  if (Number.isNaN(config.countdownLeadMs) || config.countdownLeadMs < 0) {
    throw new RangeError(`adhan countdownLeadMs must be >= 0, got ${String(config.countdownLeadMs)}`)
  }
}

/** The most recent scheduled adhan prayer at or before `now`. */
function windowAt(schedule: AdhanSchedule, now: number, config: AdhanConfig): PrayerAt | null {
  if (isPrayerSettings(schedule)) {
    try {
      return currentPrayer(now, schedule, config.prayers)
    } catch {
      // No prayer within the scan window (polar night, or an empty prayer
      // list). No window means no overlay, which is the safe answer.
      return null
    }
  }
  let best: PrayerAt | null = null
  for (const e of schedule) {
    if (!config.prayers.includes(e.prayer)) continue
    if (e.at > now) continue
    if (best === null || e.at > best.at) best = e
  }
  return best
}

/**
 * The overlay reducer. Pure: same schedule, same dismissals, same `now`, same
 * config gives the same state on every device, forever.
 *
 * The dismissal test is on window identity, which is what makes the headline
 * invariant hold by construction: once P's window is dismissed, no value of
 * `now` can make the overlay visible for that P again. It comes back at the
 * next prayer because that is a different window.
 */
export function adhanState(
  prayerTimes: AdhanSchedule,
  dismissals: readonly AdhanDismissal[],
  now: number,
  config: AdhanConfig = DEFAULT_ADHAN_CONFIG,
): AdhanOverlayState {
  requireInstant(now, 'adhanState')
  requireConfig(config)
  const win = windowAt(prayerTimes, now, config)
  if (win === null) return NO_OVERLAY

  const dismissed = dismissals.some(d => d.prayerAt === win.at && d.prayer === win.prayer)
  const expired = now - win.at >= config.visibleForMs
  return {
    visible: !dismissed && !expired,
    prayer: win.prayer,
    since: win.at,
    windowKey: adhanWindowKey(win.prayer, win.at),
    dismissed,
    expired,
  }
}

/**
 * The dismissal a tap produces, or null when there is no window to dismiss.
 * Recording a tap on an already-expired window is harmless and deliberate: it
 * keeps the log honest about what the user actually did.
 */
export function dismissAdhan(state: AdhanOverlayState, now: number): AdhanDismissal | null {
  if (state.prayer === null || state.since === null) return null
  return { prayer: state.prayer, prayerAt: state.since, at: now }
}

/**
 * Append a dismissal idempotently. Replaying the same mutation — which the
 * offline outbox will do — returns the very same array, so nothing changes.
 */
export function applyDismissal(
  dismissals: readonly AdhanDismissal[],
  dismissal: AdhanDismissal,
): readonly AdhanDismissal[] {
  const already = dismissals.some(
    d => d.prayerAt === dismissal.prayerAt && d.prayer === dismissal.prayer,
  )
  return already ? dismissals : [...dismissals, dismissal]
}

/** Whether this window has already been dismissed. */
export function isDismissed(
  dismissals: readonly AdhanDismissal[],
  prayer: PrayerName,
  prayerAt: number,
): boolean {
  return dismissals.some(d => d.prayer === prayer && d.prayerAt === prayerAt)
}

export const DEFAULT_DISMISSAL_RETENTION_MS = 7 * 24 * 60 * 60_000

/**
 * Drop dismissals for windows older than `keepMs`. Bounded memory with no
 * behavioural change: a window that old can never be the current one.
 */
export function pruneDismissals(
  dismissals: readonly AdhanDismissal[],
  now: number,
  keepMs: number = DEFAULT_DISMISSAL_RETENTION_MS,
): AdhanDismissal[] {
  return dismissals.filter(d => d.prayerAt >= now - keepMs)
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. The pre-adhan countdown — separate, and never dismissed
// ─────────────────────────────────────────────────────────────────────────────

export interface PrayerCountdown {
  prayer: PrayerName
  /** UTC epoch ms of the prayer being counted down to. */
  at: number
  /** Always > 0. */
  remainingMs: number
  /** Within `config.countdownLeadMs`. Drives the widget's score spike. */
  imminent: boolean
}

/**
 * The countdown to the next adhan prayer.
 *
 * Takes no dismissals, by design. Dismissing the overlay silences the overlay;
 * it must not blank the countdown, which is a passive dashboard element.
 */
export function prayerCountdown(
  now: number,
  prayerTimes: AdhanSchedule,
  config: AdhanConfig = DEFAULT_ADHAN_CONFIG,
): PrayerCountdown | null {
  requireInstant(now, 'prayerCountdown')
  requireConfig(config)
  let next: PrayerAt | null = null
  if (isPrayerSettings(prayerTimes)) {
    try {
      next = nextPrayer(now, prayerTimes, config.prayers)
    } catch {
      return null
    }
  } else {
    for (const e of prayerTimes) {
      if (!config.prayers.includes(e.prayer)) continue
      if (e.at <= now) continue
      if (next === null || e.at < next.at) next = e
    }
  }
  if (next === null) return null
  const remainingMs = next.at - now
  return {
    prayer: next.prayer,
    at: next.at,
    remainingMs,
    imminent: remainingMs <= config.countdownLeadMs,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Qibla (docs/02 §5)
// ─────────────────────────────────────────────────────────────────────────────

/** The Kaaba. */
export const KAABA = Object.freeze({ latitude: 21.4225, longitude: 39.8262 })

/**
 * Cairo's magnetic declination, degrees east. Hardcoded on purpose: shipping a
 * World Magnetic Model costs ~19 KB for a correction that drifts a fraction of
 * a degree per year (docs/02 §5). Revisit every few years.
 */
export const CAIRO_MAGNETIC_DECLINATION_DEG = 4.8

/** The office's qibla, degrees clockwise from TRUE north (docs/02 §5). */
export const OFFICE_QIBLA_TRUE_DEG = 137.2226

/**
 * Great-circle initial bearing from (lat, lon) to the Kaaba, degrees clockwise
 * from true north, in [0, 360).
 *
 * Implemented here rather than taken from `adhan` so the constant on the wall
 * has a derivation the codebase owns; the test asserts the two agree to within
 * 0.001°.
 */
export function qiblaTrueBearing(
  lat: number,
  lon: number,
  target: { latitude: number; longitude: number } = KAABA,
): Deg {
  const phi1 = degToRad(deg(lat))
  const phi2 = degToRad(deg(target.latitude))
  const dLambda = degToRad(deg(target.longitude - lon))
  const y = Math.sin(dLambda) * Math.cos(phi2)
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda)
  return normDeg(radToDeg(rad(Math.atan2(y, x))))
}

/** Magnetic compass heading corrected to true north. Degrees, [0, 360). */
export function magneticToTrueHeading(
  headingMagneticDeg: number,
  declinationDeg: number = CAIRO_MAGNETIC_DECLINATION_DEG,
): Deg {
  return normDeg(headingMagneticDeg + declinationDeg)
}

/**
 * 'auto' mode. The device compass reports MAGNETIC north; correct by the local
 * declination to get true north, then subtract from the qibla bearing.
 *
 * The result is the needle's on-screen rotation, degrees CLOCKWISE from the
 * top of the screen: 0 means the qibla is straight ahead of the tablet.
 */
export function magneticHeadingToQibla(
  deviceHeadingMagnetic: number,
  declinationDeg: number = CAIRO_MAGNETIC_DECLINATION_DEG,
  qiblaTrueDeg: number = OFFICE_QIBLA_TRUE_DEG,
): Deg {
  return normDeg(qiblaTrueDeg - (deviceHeadingMagnetic + declinationDeg))
}

/** Signed shortest angular difference from → to, in (-180, 180]. */
export function shortestAngleDeg(fromDeg: number, toDeg: number): Deg {
  const d = normDeg(toDeg - fromDeg)
  return (d > 180 ? d - 360 : d) as Deg
}

export type QiblaMode = 'auto' | 'fixed'

/** Live needle. Requires a magnetometer; most cheap tablets lack one. */
export interface QiblaAutoInput {
  mode: 'auto'
  /** Raw compass heading, degrees clockwise from MAGNETIC north. */
  headingMagneticDeg: number
  declinationDeg: number
  qiblaTrueDeg: number
}

/**
 * Static arrow at a user-set SCREEN angle, used verbatim. Point the tablet at
 * the qibla once, drag the arrow until it matches, save. Correct as long as
 * the tablet stays in that orientation on that wall.
 */
export interface QiblaFixedInput {
  mode: 'fixed'
  fixedAngleDeg: number
}

export type QiblaInput = QiblaAutoInput | QiblaFixedInput

export interface QiblaNeedle {
  mode: QiblaMode
  /** Rotation to apply to the arrow, degrees clockwise from screen-up. */
  screenAngleDeg: Deg
  /** The geographic bearing behind the reading. Null in fixed mode. */
  trueBearingDeg: Deg | null
  /** True when the needle tracks the device. False for the fixed arrow. */
  live: boolean
}

export function qiblaNeedle(input: QiblaInput): QiblaNeedle {
  if (input.mode === 'fixed') {
    return {
      mode: 'fixed',
      screenAngleDeg: normDeg(input.fixedAngleDeg),
      trueBearingDeg: null,
      live: false,
    }
  }
  return {
    mode: 'auto',
    screenAngleDeg: magneticHeadingToQibla(
      input.headingMagneticDeg,
      input.declinationDeg,
      input.qiblaTrueDeg,
    ),
    trueBearingDeg: normDeg(input.qiblaTrueDeg),
    live: true,
  }
}

/**
 * 'auto' requires a magnetometer. Fall back rather than show a needle that
 * lies (docs/02 §5).
 */
export function resolveQiblaMode(configured: QiblaMode, hasMagnetometer: boolean): QiblaMode {
  return configured === 'auto' && hasMagnetometer ? 'auto' : 'fixed'
}

/**
 * Build the needle input from persisted settings plus a live heading.
 *
 * ALL THREE inputs are sanitised, because all three come from outside the
 * domain — the sensor from the hardware, the other two from a settings row that
 * arrived over sync and was written by some other device:
 *
 *  - A magnetometer that reports NaN (a cheap sensor next to the magnetic wall
 *    mount docs/02 §5 warns about) is not a magnetometer. It falls back to the
 *    fixed arrow, exactly as an absent one does — the alternative is a NaN
 *    rotation, which is not a needle that lies, it is no needle at all.
 *  - A corrupt stored fixed angle degrades to 0 (straight up) rather than NaN,
 *    so the arrow is drawn and visibly wrong instead of vanishing.
 *  - COORDINATES THAT ARE NOT NUMBERS ARE NOT A LOCATION. There is no true
 *    bearing without them, so 'auto' falls back to the fixed arrow rather than
 *    computing one from whatever `Math.sin` makes of the value. This is the
 *    worst of the three if it is missed, because the failure is not visible: a
 *    null latitude coerces to 0 and produces a confident, live-badged needle
 *    pointing 20° instead of 137°, which is a needle that lies.
 */
export function qiblaFromSettings(
  settings: Pick<Settings, 'qiblaMode' | 'qiblaFixedAngleDeg' | 'latitude' | 'longitude'>,
  sensor: { hasMagnetometer: boolean; headingMagneticDeg: number | null },
  declinationDeg: number = CAIRO_MAGNETIC_DECLINATION_DEG,
): QiblaNeedle {
  const heading = sensor.headingMagneticDeg
  const usable = sensor.hasMagnetometer && heading !== null && Number.isFinite(heading)
  const located =
    Number.isFinite(settings.latitude) &&
    Number.isFinite(settings.longitude) &&
    Math.abs(settings.latitude) <= 90
  const mode = located ? resolveQiblaMode(settings.qiblaMode, usable) : 'fixed'
  if (mode === 'fixed') {
    const fixed = settings.qiblaFixedAngleDeg
    return qiblaNeedle({ mode: 'fixed', fixedAngleDeg: Number.isFinite(fixed) ? fixed : 0 })
  }
  return qiblaNeedle({
    mode: 'auto',
    headingMagneticDeg: heading ?? 0,
    declinationDeg,
    qiblaTrueDeg: qiblaTrueBearing(settings.latitude, settings.longitude),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. Ramadan (docs/01 §8, docs/02 §4)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_IMSAK_OFFSET_MINUTES = 10

/**
 * HAND-MAINTAINED TABLE — CONFIRM EVERY YEAR, BEFORE SHAABAN ENDS.
 *
 * Egypt determines Ramadan by LOCAL SIGHTING via Dar al-Ifta, not by
 * calculation, so no offline computation is authoritative and this table is
 * data, not a derivation (docs/02 §4).
 *
 * `year` is the HIJRI year, not the Gregorian one: a Ramadan can straddle two
 * Gregorian years, and the Hijri year is the only key that stays unique.
 *
 * 1447 AH / 2026 CE — PROVISIONAL, both ends:
 *   - START. The calculated Umm al-Qura calendar gives 18 Feb 2026, which is
 *     independently confirmed. Egypt's announcement of 19 Feb 2026 is the
 *     plausible local-sighting outcome but was NOT confirmable from a primary
 *     source (docs/02 §4). 19 Feb is used here because the office is in Egypt
 *     and follows Dar al-Ifta. Replace it with the actual announcement.
 *   - END. Assumes a 30-day month. Ramadan is 29 or 30 days and which one it
 *     is is not known until the Shawwal sighting, so this end date will need
 *     correcting in the last week of the month.
 *
 * Use `applyRamadanOverride` to correct either end from the front end without
 * shipping a new build.
 */
export const RAMADAN_PERIODS: readonly RamadanPeriod[] = Object.freeze([
  Object.freeze({
    year: 1447,
    startDay: '2026-02-19' as DayKey,
    endDay: '2026-03-20' as DayKey,
    source: 'manual',
    imsakOffsetMinutes: DEFAULT_IMSAK_OFFSET_MINUTES,
  } as RamadanPeriod),
])

/**
 * A period is well-formed when both keys are real dates, start <= end, the
 * Hijri year is a whole number and the imsak offset is a whole number of
 * minutes that is not negative.
 *
 * Both of the last two are load-bearing, not decoration:
 *
 *  - `applyRamadanOverride` replaces BY YEAR, and `NaN !== NaN`, so a period
 *    with a non-numeric year could never be replaced — each re-application of
 *    the same override would append another copy and the two offline devices
 *    that docs/02 §4 cares about would diverge instead of converging.
 *  - A negative offset would put imsak AFTER fajr, and `fastWindow` throws on
 *    it. Letting such a period into the table means `ramadanState` throws at
 *    render time, taking the whole dashboard down with it. Reject at the door.
 */
export function isValidRamadanPeriod(p: RamadanPeriod): boolean {
  try {
    parseDayKey(p.startDay)
    parseDayKey(p.endDay)
  } catch {
    return false
  }
  if (!Number.isInteger(p.year)) return false
  if (!Number.isInteger(p.imsakOffsetMinutes) || p.imsakOffsetMinutes < 0) return false
  return p.startDay <= p.endDay
}

/**
 * Replace the period for the same Hijri year, or add it. This is the manual
 * override: Dar al-Ifta announces, someone types it in, and the shipped guess
 * is superseded without a new build.
 *
 * Idempotent — applying the same override twice is indistinguishable from
 * applying it once — and total-ordered by start date, so two devices given the
 * same overrides in any order end up with the same table.
 */
export function applyRamadanOverride(
  periods: readonly RamadanPeriod[],
  override: RamadanPeriod,
): RamadanPeriod[] {
  if (!isValidRamadanPeriod(override)) {
    throw new RangeError(`invalid Ramadan period for ${override.year}: ${override.startDay}..${override.endDay}`)
  }
  const kept = periods.filter(p => p.year !== override.year)
  return [...kept, override].sort((a, b) => (a.startDay < b.startDay ? -1 : a.startDay > b.startDay ? 1 : a.year - b.year))
}

/** The period containing `day`, or null. Both ends inclusive. */
export function ramadanPeriodFor(
  day: DayKey,
  periods: readonly RamadanPeriod[] = RAMADAN_PERIODS,
): RamadanPeriod | null {
  for (const p of periods) {
    if (day >= p.startDay && day <= p.endDay) return p
  }
  return null
}

/**
 * Is `day` inside Ramadan? Compares by day key, so it answers in whatever
 * reckoning the caller's key is in — pass an office rollover day for "is the
 * board in Ramadan mode", a calendar date for astronomical questions.
 */
export function isRamadan(
  day: DayKey,
  periods: readonly RamadanPeriod[] = RAMADAN_PERIODS,
): boolean {
  return ramadanPeriodFor(day, periods) !== null
}

/** Is the office in Ramadan right now? Uses the 05:00 rollover day. */
export function isRamadanAt(
  now: number,
  periods: readonly RamadanPeriod[] = RAMADAN_PERIODS,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): boolean {
  requireInstant(now, 'isRamadanAt')
  return isRamadan(rolloverDayOf(now, rolloverHour), periods)
}

/** 1-based day of the month, or null outside Ramadan. */
export function dayOfRamadan(
  day: DayKey,
  periods: readonly RamadanPeriod[] = RAMADAN_PERIODS,
): number | null {
  const p = ramadanPeriodFor(day, periods)
  return p === null ? null : daysBetween(p.startDay, day) + 1
}

/** Iftar — the fast on CALENDAR date `day` ends at maghrib. */
export function iftarAt(day: DayKey, settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS): number {
  return prayerTimesFor(day, settings).maghrib
}

/** Suhoor cutoff — the fast on CALENDAR date `day` begins at fajr. */
export function suhoorEndsAt(
  day: DayKey,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
): number {
  return prayerTimesFor(day, settings).fajr
}

/**
 * Imsak — a configurable safety buffer before fajr, after which eating stops.
 * A negative offset would put imsak after fajr, which is meaningless, so it is
 * rejected rather than quietly inverted.
 */
export function imsakAt(
  day: DayKey,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  imsakOffsetMinutes: number = DEFAULT_IMSAK_OFFSET_MINUTES,
): number {
  return fastWindow(day, settings, imsakOffsetMinutes).imsak
}

export interface FastWindow {
  /** CALENDAR date of the fast. */
  day: DayKey
  imsak: number
  suhoorEnds: number
  iftar: number
}

/** The three instants of one fast, on CALENDAR date `day`. */
export function fastWindow(
  day: DayKey,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  imsakOffsetMinutes: number = DEFAULT_IMSAK_OFFSET_MINUTES,
): FastWindow {
  if (!Number.isFinite(imsakOffsetMinutes) || imsakOffsetMinutes < 0) {
    throw new RangeError(`imsak offset must be >= 0 minutes, got ${String(imsakOffsetMinutes)}`)
  }
  const times = prayerTimesFor(day, settings)
  return {
    day,
    imsak: times.fajr - Math.round(imsakOffsetMinutes) * 60_000,
    suhoorEnds: times.fajr,
    iftar: times.maghrib,
  }
}

export interface RamadanState {
  /**
   * Ramadan mode, by the OFFICE (05:00 rollover) day containing `now`. This is
   * what gates widget eligibility and the `pausedInRamadan` chores, so it uses
   * the same reckoning everything else date-keyed does.
   */
  active: boolean
  /** The period the office day falls in. Null when not active. */
  period: RamadanPeriod | null
  /** 1-based day of the month by the office day. Null when not active. */
  dayOfMonth: number | null
  /**
   * CALENDAR date of the fast running or next to start — set whenever that
   * date is inside a Ramadan period, which is NOT the same condition as
   * `active`. Two edges make the distinction load-bearing:
   *
   *  - The first suhoor of Ramadan happens before the office day has rolled
   *    over into Ramadan (fajr 05:03, rollover 05:00, so at 04:00 the office
   *    day is still the day before). `active` is false there and the suhoor
   *    countdown must still show.
   *  - After the last iftar, the office day is still the last day of Ramadan
   *    but the next calendar day is Eid. `active` is true there and there must
   *    be no countdown to a fast that will not happen.
   */
  fastDay: DayKey | null
  /** 1-based day of the month of `fastDay`. */
  fastDayOfMonth: number | null
  /** now ∈ [fajr(fastDay), maghrib(fastDay)). */
  fasting: boolean
  imsakAt: number | null
  suhoorEndsAt: number | null
  iftarAt: number | null
  /** Negative once imsak has passed for the current fast. */
  msToImsak: number | null
  /** Always > 0 while `fastDay` is set. */
  msToIftar: number | null
}

const NOT_RAMADAN: RamadanState = Object.freeze({
  active: false,
  period: null,
  dayOfMonth: null,
  fastDay: null,
  fastDayOfMonth: null,
  fasting: false,
  imsakAt: null,
  suhoorEndsAt: null,
  iftarAt: null,
  msToImsak: null,
  msToIftar: null,
})

/**
 * Everything the Ramadan widgets need, from one trusted instant.
 *
 * `active` and `dayOfMonth` are in OFFICE-day reckoning; the fast instants are
 * in calendar reckoning, because a fast is [fajr(D), maghrib(D)) on one
 * calendar date. See `fastDay` for why the two are reported separately.
 */
export function ramadanState(
  now: number,
  settings: PrayerSettings = DEFAULT_PRAYER_SETTINGS,
  periods: readonly RamadanPeriod[] = RAMADAN_PERIODS,
): RamadanState {
  requireInstant(now, 'ramadanState')
  const officeDay = rolloverDayOf(now, settings.rolloverHour)
  const period = ramadanPeriodFor(officeDay, periods)

  // The fast in play is today's until its maghrib has passed, then tomorrow's.
  const today = calendarDayOf(now)
  const fastDay = now < prayerTimesFor(today, settings).maghrib ? today : addDays(today, 1)
  const fastPeriod = ramadanPeriodFor(fastDay, periods)

  if (period === null && fastPeriod === null) return NOT_RAMADAN

  const base = {
    active: period !== null,
    period,
    dayOfMonth: period === null ? null : daysBetween(period.startDay, officeDay) + 1,
  }
  if (fastPeriod === null) {
    return {
      ...base,
      fastDay: null,
      fastDayOfMonth: null,
      fasting: false,
      imsakAt: null,
      suhoorEndsAt: null,
      iftarAt: null,
      msToImsak: null,
      msToIftar: null,
    }
  }
  const w = fastWindow(fastDay, settings, fastPeriod.imsakOffsetMinutes)
  return {
    ...base,
    fastDay,
    fastDayOfMonth: daysBetween(fastPeriod.startDay, fastDay) + 1,
    fasting: now >= w.suhoorEnds && now < w.iftar,
    imsakAt: w.imsak,
    suhoorEndsAt: w.suhoorEnds,
    iftarAt: w.iftar,
    msToImsak: w.imsak - now,
    msToIftar: w.iftar - now,
  }
}
